import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { capitalize } from '@mui/material';
import { useRedux } from 'src/hooks/useRedux';
import { ActionModalProps } from './types';
import { RESULT_MODAL, ResultModalProps } from '../ResultModal';
import { ACTION_MODAL } from './constants';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { flattenFieldErrors } from 'src/utils/flattenFieldErrors';
import { ValidationErrorsError } from 'src/utils/serverFormErrors';


export const useModel = ({
  actionName, cols, lc, rows, definedFilters, setSelected, item, navigatePath, config = { type: 'dialog' }, onSuccess
}: ActionModalProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal, closeModal } = useRedux();
  const { schema = {}, defaultValues = {}, init, withCustomDescription, description, onValidationFailed, formFieldLabels } = config;
  const [formSchema, setFormSchema] = useState(Yup.object({ ...(schema || {}) }));
  const [defaultValuesData, setDefaultValuesData] = useState(defaultValues);
  const [initData, setInitData] = useState<Record<string, any>>();
  const [validationErrors, setValidationErrors] = useState<{ path: string; message: string }[]>([]);
  const [errorsDrawerOpen, setErrorsDrawerOpen] = useState(false);

  const methods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: defaultValues
  });


  useEffect(() => {
    const loadInit = async () => {
      
      if (init && rows?.length > 0) {
        const id = rows?.length === 1 ? rows[0].id : item?.id;
        const ids = rows.map(row => Number(row.id))
        const params = await init(Number(id), ids);

        if (params?.defaultValues) {
          methods.reset({
            ...defaultValues,
            ...(params?.defaultValues || {})
          });
          setDefaultValuesData(defaultValuesData);
        }

        if (params?.schema) {
          setFormSchema(Yup.object().shape({ ...schema, ...(params?.schema || {}) }));
        }

        if (params?.fullSchema) {
          setFormSchema(params.fullSchema);
        }

        if (params?.initData) {
          setInitData(params?.initData)
        }
      }
    }

    loadInit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


 const { handleSubmit } = methods;

  const onClose = useCallback(() => {
    closeModal(ACTION_MODAL);
  }, [closeModal])

  const onResultClose = useCallback(() => {
    if (setSelected) {
      setSelected([]);
    }
    closeModal(RESULT_MODAL);
  }, [closeModal, setSelected]);

  const lcAction = useCallback((name: string, formData: any) => {
    const action = async () => {
      if (config.onAction) {
        try {
          await config.onAction({
            formData,
            rows,
            item,
            actionName: name,
            definedFilters,
            lc,
            navigatePath,
            onClose,
            initData,
          });
          onClose();
          if (setSelected) {
            setSelected([]);
          }
          onSuccess?.();
          enqueueSnackbar(`${capitalize(name)} was completed successfully`, { variant: 'success' });
        } catch (error: any) {
          if (error instanceof ValidationErrorsError && error.validationErrors?.length > 0) {
            setValidationErrors(error.validationErrors);
            setErrorsDrawerOpen(true);
          } else {
            enqueueSnackbar(error?.message || `${capitalize(name)} failed`, { variant: 'error' });
          }
        }
        return;
      }

      onClose();

      const data = config.onSubmit ? config.onSubmit(formData) : undefined;

      const result = await lc(
        item?.id ? [Number(item.id)] : rows.map(({ id }) => Number(id)),
        name,
        !!item?.id,
        definedFilters,
        data
      );

      const singleNotify =
        result?.succeed?.length > 0
          ? `${capitalize(name)} was completed successfully`
          : `${capitalize(name)} was not performed or an error occurred during execution`;

      if (item?.id && name === 'delete') {
        navigate(navigatePath.scroller);
        enqueueSnackbar(singleNotify, {
          variant: result?.succeed?.length > 0 ? 'success' : 'error',
        });
      } else {
        if (item?.id || rows.length === 1) {
          enqueueSnackbar(singleNotify, {
            variant: result?.succeed?.length > 0 ? 'success' : 'error',
          });
          if (setSelected) {
            setSelected([]);
          }
        } else {
          showModal<ResultModalProps>(RESULT_MODAL, {
            succeed: rows.filter((item) => result?.succeed?.includes(item.id)),
            failed: rows.filter((item) => result?.failed?.includes(item.id)),
            cols,
            title: `${capitalize(name)} action result`,
            onClose: onResultClose
          });
        }
      }

      return result;
    };
    action();
  }, [cols, config, definedFilters, enqueueSnackbar,
    initData, item?.id, lc, navigate, navigatePath.scroller,
    onClose, onResultClose, onSuccess, rows, setSelected, showModal
  ])

  const handleAction = useCallback(
    (data: any) => {
      if (data !== undefined) {
        lcAction(actionName, data);
      }
    },
    [actionName, lcAction]
  );

  const handleValidationFailed = useCallback(
    (errors: Record<string, any>) => {
      onValidationFailed?.(errors);
      const flat = flattenFieldErrors(errors);
      const first = flat[0];
      if (first?.message) {
        enqueueSnackbar(first.message, { variant: 'error' });
      }
    },
    [onValidationFailed, enqueueSnackbar]
  );

  return {
    onClose,
    title: withCustomDescription ? description : `Do you want to ${capitalize(actionName)}?`,
    rows,
    onAction: handleSubmit(handleAction, handleValidationFailed),
    config,
    methods,
    cols,
    defaultValuesData,
    initData,
    validationErrors,
    errorsDrawerOpen,
    onCloseErrorsDrawer: () => setErrorsDrawerOpen(false),
    formFieldLabels,
  };
}
